// /home/ubuntu/avaai_dashboard/source/src/pages/games/CheckersGame.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Text } from "@react-three/drei";
import "../../styles/GameLayout.css";

import { useNavigate } from "react-router-dom";
interface Piece {
  id: number;
  x: number;
  y: number;
  color: "red" | "blue";
  king: boolean;
}

const BOARD_SIZE = 8;
type PlayerColor = "red" | "blue";

function isPlayableSquare(x: number, y: number) {
  // Standard checkers: pieces live only on playable dark squares.
  return (x + y) % 2 === 1;
}

function makeInitialPieces(): Piece[] {
  const init: Piece[] = [];
  let id = 0;

  // Ava / Blue: back 3 rows.
  for (let y = 0; y <= 2; y++) {
    for (let x = 0; x < BOARD_SIZE; x++) {
      if (isPlayableSquare(x, y)) {
        init.push({ id: id++, x, y, color: "blue", king: false });
      }
    }
  }

  // User / Red: front 3 rows.
  for (let y = 5; y <= 7; y++) {
    for (let x = 0; x < BOARD_SIZE; x++) {
      if (isPlayableSquare(x, y)) {
        init.push({ id: id++, x, y, color: "red", king: false });
      }
    }
  }

  return init;
}

function inside(x: number, y: number) {
  return x >= 0 && y >= 0 && x < BOARD_SIZE && y < BOARD_SIZE;
}

function occupant(all: Piece[], x: number, y: number) {
  return all.find((p) => p.x === x && p.y === y);
}

function dirsFor(piece: Piece, jump = false) {
  const step = jump ? 2 : 1;
  if (piece.king) return [[step, step], [-step, step], [step, -step], [-step, -step]];
  const dy = piece.color === "red" ? -step : step;
  return [[step, dy], [-step, dy]];
}

function legalMovesFor(piece: Piece, all: Piece[], jumpsOnly = false) {
  const moves: { x: number; y: number; captureId?: number }[] = [];

  for (const [dx, dy] of dirsFor(piece, false)) {
    const x = piece.x + dx;
    const y = piece.y + dy;
    if (!jumpsOnly && inside(x, y) && !occupant(all, x, y)) {
      moves.push({ x, y });
    }
  }

  for (const [dx, dy] of dirsFor(piece, true)) {
    const x = piece.x + dx;
    const y = piece.y + dy;
    const midX = piece.x + dx / 2;
    const midY = piece.y + dy / 2;
    const jumped = occupant(all, midX, midY);
    if (inside(x, y) && !occupant(all, x, y) && jumped && jumped.color !== piece.color) {
      moves.push({ x, y, captureId: jumped.id });
    }
  }

  return moves;
}

function allLegalMoves(color: PlayerColor, all: Piece[]) {
  const pieces = all.filter((p) => p.color === color);
  const jumps = pieces.flatMap((p) =>
    legalMovesFor(p, all, true)
      .filter((m) => m.captureId !== undefined)
      .map((m) => ({ piece: p, move: m }))
  );

  if (jumps.length) return jumps;

  return pieces.flatMap((p) =>
    legalMovesFor(p, all, false).map((m) => ({ piece: p, move: m }))
  );
}

function winnerFor(all: Piece[]): PlayerColor | null {
  const redPieces = all.some((p) => p.color === "red");
  const bluePieces = all.some((p) => p.color === "blue");

  if (!redPieces) return "blue";
  if (!bluePieces) return "red";

  if (allLegalMoves("red", all).length === 0) return "blue";
  if (allLegalMoves("blue", all).length === 0) return "red";

  return null;
}

export default function CheckersGame() {
  const navigate = useNavigate();
  const [pieces, setPieces] = useState<Piece[]>(() => makeInitialPieces());
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [turn, setTurn] = useState<PlayerColor>("red");
  const [winner, setWinner] = useState<PlayerColor | null>(null);
  const [message, setMessage] = useState("You are red. Ava is blue. Red moves first.");
  const [aiLevel, setAiLevel] = useState<number>(2);

  const selected = pieces.find((p) => p.id === selectedId) || null;
  const forcedMoves = useMemo(
    () => allLegalMoves(turn, pieces).filter((x) => x.move.captureId !== undefined),
    [turn, pieces]
  );

  function applyMove(piece: Piece, move: { x: number; y: number; captureId?: number }, nextTurn = true) {
    let updated = pieces
      .filter((p) => p.id !== move.captureId)
      .map((p) =>
        p.id === piece.id
          ? {
              ...p,
              x: move.x,
              y: move.y,
              king: p.king || (p.color === "red" && move.y === 0) || (p.color === "blue" && move.y === 7),
            }
          : p
      );

    const moved = updated.find((p) => p.id === piece.id)!;
    const chainJumps = move.captureId !== undefined
      ? legalMovesFor(moved, updated, true).filter((m) => m.captureId !== undefined)
      : [];

    const win = winnerFor(updated);

    setPieces(updated);

    if (win) {
      setWinner(win);
      setSelectedId(null);
      setMessage(win === "red" ? "Congratulations! You beat Ava. Want a rematch?" : "Ava wins. Good game — want a rematch?");
      return;
    }

    if (chainJumps.length && piece.color === "red") {
      setSelectedId(moved.id);
      setMessage("Forced jump: you must keep jumping with the same piece.");
      return;
    }

    setSelectedId(null);

    if (nextTurn) {
      const nt = piece.color === "red" ? "blue" : "red";
      setTurn(nt);
      setMessage(nt === "blue" ? "Ava is thinking..." : "Your turn.");
    }
  }

  function handleSquareClick(x: number, y: number) {
    if (winner || turn !== "red") return;

    if (selected) {
      const legal = legalMovesFor(selected, pieces, forcedMoves.length > 0);
      const move = legal.find((m) => m.x === x && m.y === y);

      if (!move) {
        setMessage(forcedMoves.length ? "A jump is available, so you must jump." : "That move is not legal.");
        return;
      }

      applyMove(selected, move);
      return;
    }

    const piece = occupant(pieces, x, y);
    if (!piece || piece.color !== "red") return;

    if (forcedMoves.length && !forcedMoves.some((fm) => fm.piece.id === piece.id)) {
      setMessage("Forced jump rule: select a piece that can jump.");
      return;
    }

    setSelectedId(piece.id);
    setMessage(forcedMoves.length ? "Jump available — forced jump is active." : "Piece selected.");
  }

  useEffect(() => {
    if (winner || turn !== "blue") return;

    const timer = window.setTimeout(() => {
      const legal = allLegalMoves("blue", pieces);
      if (!legal.length) {
        setWinner("red");
        setMessage("Congratulations! Ava has no legal moves. Want a rematch?");
        return;
      }

      const jumps = legal.filter((x) => x.move.captureId !== undefined);
      const choices = jumps.length ? jumps : legal;

      // Ava prefers jumps and kinging, then random among best candidates.
      const scored = choices.map((choice) => {
        let score = choice.move.captureId !== undefined ? 100 : 0;
        if (!choice.piece.king && choice.move.y === 7) score += 50;
        score += Math.floor(Math.random() * (aiLevel + 2));
        return { ...choice, score };
      });

      scored.sort((a, b) => b.score - a.score);
      const best = scored[0];

      applyMove(best.piece, best.move);
    }, 650);

    return () => window.clearTimeout(timer);
  }, [turn, pieces, winner, aiLevel]);

  function restart() {
    setPieces(makeInitialPieces());
    setSelectedId(null);
    setTurn("red");
    setWinner(null);
    setMessage("New game. You are red. Ava is blue. Red moves first.");
  }

  return (
    <div className="game-hub-container">
      <h1 className="neon-title">🕹️ AVAAIO Checkers</h1>

      <p className="neon-subtitle">
        Turn: <span style={{ color: turn === "red" ? "#ff3366" : "#00e0ff" }}>
          {turn === "red" ? "YOU / RED" : "AVA / BLUE"}
        </span>
      </p>

      <p className="neon-subtitle">{message}</p>

      {winner && (
        <div>
          <h2 className="neon-title">
            {winner === "red" ? "🎉 Congratulations — You Win!" : "🤖 Ava Wins!"}
          </h2>
          <p className="neon-subtitle">Want a rematch?</p>
        </div>
      )}

      <div style={{ width: "88%", height: "70vh", margin: "20px auto" }}>
        <Canvas camera={{ position: [4, 9, 10], fov: 48 }}>
          <color attach="background" args={["black"]} />
          <ambientLight intensity={0.55} />
          <pointLight position={[10, 10, 10]} color="#00ffff" intensity={1.1} />
          <OrbitControls enablePan={false} enableZoom={true} />

          {[...Array(BOARD_SIZE)].map((_, y) =>
            [...Array(BOARD_SIZE)].map((_, x) => {
              const dark = isPlayableSquare(x, y);
              const piece = occupant(pieces, x, y);
              const isSelected = selectedId !== null && piece?.id === selectedId;

              return (
                <mesh
                  key={`${x}-${y}`}
                  position={[x - 3.5, 0, y - 3.5]}
                  onClick={() => handleSquareClick(x, y)}
                >
                  <boxGeometry args={[1, 0.1, 1]} />
                  <meshStandardMaterial
                    emissive={isSelected ? "#ffff00" : dark ? "#111111" : "#00ffff"}
                    emissiveIntensity={isSelected ? 0.8 : dark ? 0.04 : 0.28}
                    color={dark ? "#050505" : "#003344"}
                  />

                  {piece && (
                    <group position={[0, 0.22, 0]}>
                      <mesh>
                        <cylinderGeometry args={[0.38, 0.38, 0.18, 40]} />
                        <meshStandardMaterial
                          emissive={piece.color === "red" ? "#ff3366" : "#00e0ff"}
                          emissiveIntensity={0.85}
                          color={piece.color === "red" ? "#ff3366" : "#00e0ff"}
                        />
                      </mesh>

                      {piece.king && (
                        <>
                          <mesh position={[0, 0.16, 0]}>
                            <torusGeometry args={[0.24, 0.045, 16, 80]} />
                            <meshStandardMaterial emissive="#ffff00" emissiveIntensity={1} color="#ffff00" />
                          </mesh>
                          <Text position={[0, 0.33, 0]} fontSize={0.22} color="#ffff00" anchorX="center" anchorY="middle">
                            K
                          </Text>
                        </>
                      )}
                    </group>
                  )}
                </mesh>
              );
            })
          )}
        </Canvas>
      </div>

      <div style={{ marginTop: "20px" }}>
        <label>
          Ava Intelligence Level: {aiLevel}
          <input
            type="range"
            min="1"
            max="5"
            value={aiLevel}
            onChange={(e) => setAiLevel(parseInt(e.target.value))}
          />
        </label>
      </div>

      <div style={{ marginTop: "20px" }}>
        <button className="neon-btn" onClick={restart}>
          🔄 Rematch
        </button>
        <button className="exit-btn" onClick={() => navigate("/games")}>
          ⏪ Exit to Games
        </button>
      </div>
    </div>
  );
}
